import {
  ApplicationAudio,
  INPUT_SLOTS,
  InputCaptureError,
  type InputSlot,
} from "../audio/applicationAudio"
import { OutputRouteError } from "../audio/router"
import { bindDirectionControls } from "./directionControls"
import { buttonElement, htmlElement, inputElement, selectElement } from "./dom"
import { bindGeneratorControls } from "./generatorControls"

type StatusKind = "info" | "success" | "warning" | "error"

let audio: ApplicationAudio | undefined

function setStatus(message: string, kind: StatusKind = "info"): void {
  const status = htmlElement("global-status")
  status.textContent = message
  status.className = `status status-${kind}`
}

function audioApplication(): ApplicationAudio {
  audio ??= new ApplicationAudio()
  return audio
}

function selectedDevice(slot: InputSlot): string {
  return selectElement(`input-${slot}`).value
}

function replaceDeviceOptions(
  target: HTMLSelectElement,
  devices: readonly MediaDeviceInfo[],
  offAllowed: boolean,
): void {
  const selected = target.value
  const options = devices.map(
    (device, index) => new Option(device.label || `Microphone ${index + 1}`, device.deviceId),
  )
  const first = offAllowed ? new Option("Off", "") : new Option("System default", "default")
  target.replaceChildren(first, ...options)
  if ([...target.options].some((option) => option.value === selected)) target.value = selected
}

async function refreshDevices(): Promise<void> {
  const devices = await audioApplication().listDevices()
  const inputs = devices.filter((device) => device.kind === "audioinput")
  const outputs = devices.filter((device) => device.kind === "audiooutput")
  replaceDeviceOptions(selectElement("input-a"), inputs, false)
  replaceDeviceOptions(selectElement("input-b"), inputs, true)
  replaceDeviceOptions(selectElement("input-c"), inputs, true)
  const outputSelect = selectElement("output-device")
  const selectedOutput = outputSelect.value
  outputSelect.replaceChildren(
    new Option("System default", "default"),
    ...outputs.map(
      (device, index) => new Option(device.label || `Output ${index + 1}`, device.deviceId),
    ),
  )
  if ([...outputSelect.options].some((option) => option.value === selectedOutput)) {
    outputSelect.value = selectedOutput
  }
  htmlElement("device-count").textContent = `${inputs.length} in / ${outputs.length} out`
}

async function startSlot(slot: InputSlot): Promise<number | undefined> {
  const deviceId = selectedDevice(slot)
  if (deviceId === "") {
    audioApplication().stopInput(slot)
    htmlElement(`input-${slot}-state`).textContent = "Off"
    return undefined
  }
  const channels = await audioApplication().startInput(slot, deviceId)
  htmlElement(`input-${slot}-state`).textContent = `${channels} channel${channels === 1 ? "" : "s"}`
  return channels
}

async function toggleAudio(): Promise<void> {
  const app = audioApplication()
  const button = buttonElement("audio-toggle")
  if (app.running) {
    app.stopAllInputs()
    buttonElement("audio-toggle").classList.add("button-primary")
    htmlElement("audio-button-label").textContent = "Start audio"
    htmlElement("input-readout").textContent = "Awaiting input"
    for (const slot of INPUT_SLOTS)
      htmlElement(`input-${slot}-state`).textContent = slot === "a" ? "Idle" : "Off"
    setStatus("Audio stopped. All microphone tracks were released.", "success")
    return
  }
  await app.resume()
  const primaryChannels = await startSlot("a")
  await Promise.all(INPUT_SLOTS.slice(1).map((slot) => startSlot(slot)))
  await refreshDevices()
  button.classList.remove("button-primary")
  htmlElement("audio-button-label").textContent = "Stop audio"
  htmlElement("input-readout").textContent = "Input A active"
  setStatus(
    `Input A active (${primaryChannels ?? 1} channel${primaryChannels === 1 ? "" : "s"}).`,
    "success",
  )
}

function handleBoundaryError(cause: unknown): void {
  if (cause instanceof InputCaptureError || cause instanceof OutputRouteError) {
    setStatus(`${cause.name}: ${cause.message}`, "error")
    return
  }
  if (cause instanceof Error) {
    setStatus(cause.message, "error")
    return
  }
  setStatus("An unknown audio error occurred.", "error")
}

export function bindAudioControls(): () => void {
  const stopDirectionControls = bindDirectionControls(audioApplication)
  bindGeneratorControls(audioApplication, handleBoundaryError)
  buttonElement("audio-toggle").addEventListener(
    "click",
    () => void toggleAudio().catch(handleBoundaryError),
  )
  buttonElement("choose-output").addEventListener("click", () => {
    void audioApplication()
      .router.pickOutput()
      .then((device) => {
        if (device === undefined) {
          setStatus(
            "This browser cannot prompt for an output device. Use the available output list or the system default.",
            "warning",
          )
          return
        }
        const output = selectElement("output-device")
        output.add(new Option(device.label || "Selected output", device.deviceId))
        output.value = device.deviceId
        return audioApplication()
          .router.setOutput(device.deviceId)
          .then(() => {
            setStatus(`Output route: ${device.label || "Selected output"}.`, "success")
          })
      })
      .catch(handleBoundaryError)
  })
  selectElement("output-device").addEventListener("change", (event) => {
    if (event.currentTarget instanceof HTMLSelectElement) {
      const target = event.currentTarget
      void audioApplication()
        .router.setOutput(target.value)
        .then(() => {
          setStatus(
            `Output route: ${target.selectedOptions[0]?.textContent ?? "System default"}.`,
            "success",
          )
        })
        .catch(handleBoundaryError)
    }
  })
  inputElement("monitor-inputs").addEventListener("change", () => {
    const enabled = inputElement("monitor-inputs").checked
    const level = inputElement("monitor-level").valueAsNumber
    audioApplication().router.setMonitoring(enabled, level)
    setStatus(
      enabled ? `Input monitoring enabled at −${Math.abs(level)} dB.` : "Input monitoring muted.",
      enabled ? "warning" : "success",
    )
  })
  for (const slot of INPUT_SLOTS) {
    selectElement(`input-${slot}`).addEventListener("change", () => {
      if (audio?.running) {
        void startSlot(slot)
          .then((channels) => {
            if (channels !== undefined)
              setStatus(
                `Input ${slot.toUpperCase()} active (${channels} channel${channels === 1 ? "" : "s"}).`,
                "success",
              )
          })
          .catch(handleBoundaryError)
      }
    })
  }
  navigator.mediaDevices.addEventListener(
    "devicechange",
    () => void refreshDevices().catch(handleBoundaryError),
  )
  inputElement("monitor-level").addEventListener("input", () => {
    htmlElement("monitor-level-value").textContent =
      `${inputElement("monitor-level").valueAsNumber} dB`
    audioApplication().router.setMonitoring(
      inputElement("monitor-inputs").checked,
      inputElement("monitor-level").valueAsNumber,
    )
  })
  const capability = audioApplication().router.capabilities
  htmlElement("routing-capability").textContent = capability.contextSink
    ? "Direct sink routing"
    : capability.mediaElementSink
      ? "Media sink routing"
      : "System output only"
  return () => {
    stopDirectionControls()
    audio?.close()
  }
}

export function currentAudio(): ApplicationAudio | undefined {
  return audio
}

export function ensureAudio(): ApplicationAudio {
  return audioApplication()
}
